import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  elementY,
  observe,
  resizeEvent,
  subscribe,
  useObserver,
  viewportY,
} from 'react-ui-observer'
import { BLOCKS, type Document } from '@contentful/rich-text-types'
import type { Entry } from 'contentful'
import get from 'lodash/get'
import styled, { css } from 'styled-components'

import { triggerPageView } from 'analytics/events'
import type {
  IGradient,
  IMetadata,
  IPageStoriesFields,
  IPageStoryFields,
  IStoryimage,
} from 'contentful-shared'
import { breakpoints } from 'bl-common/src/constants/breakpoints'
import { colors } from 'bl-common/src/constants/colors'
import {
  base,
  gridOffsetFrom,
  gridOffsetTo,
} from 'bl-common/src/constants/sizes'
import { Type } from 'bl-common/src/elements/Typography/Typography'
import { RichTextRenderer } from 'bl-common/src/richText/RichTextRenderer'
import { Appear } from 'bl-common/src/units/Appear'
import { LightBoxItem } from 'bl-common/src/units/LightBox/LightBoxItem'
import { LightBoxProvider } from 'bl-common/src/units/LightBox/LightBoxProvider'
import { Section } from 'bl-common/src/units/Section/Section'
import { SmartImage } from 'bl-common/src/units/SmartImage/SmartImage'
import { between } from 'bl-common/src/utils/between'
import { media, mediaMax } from 'bl-common/src/utils/media'

import { withTransition } from '../../../utils/withTransition'
import { Metadata } from '../../Metadata'
import Page from '../../Page'
import NewsletterSignupFormSection from '../../TemplatePage/sections/NewsletterSignupFormSection'
import { BackToStoriesLink } from './BackToStoriesLink'
import { ExploreTopics } from './ExploreTopics'
import { estimatedReadTime } from './helpers'
import { RelatedExperiences } from './RelatedExperiences'
import { StoryArchive } from './StoryArchive'
import StoryBlockQuote from './StoryBlockQuote'
import StoryGallery from './StoryGallery'
import { StoryMeta } from './StoryMeta'
import { StoryVideo } from './StoryVideo'
import { TagCollection } from './TagCollection'

// TODO: This is the "same code" as in Appear.tsx, but this is using 'react-ui-observer', so change this when Appear has been refactored.
const intervalHandler = (onChange, ms) => {
  const intervalId = setInterval(onChange, ms) // eslint-disable-line @typescript-eslint/no-implied-eval
  return () => {
    clearInterval(intervalId)
  }
}

export const intervalEvent = ms => subscribe(ms, intervalHandler)

const visible = (wasVisible, elementY, viewportEnter, viewportExit) =>
  wasVisible ? elementY < viewportExit : elementY < viewportEnter

const includeLast = fn => {
  let lastValue = undefined
  return (...args) => {
    lastValue = fn(lastValue, ...args)
  }
}

const isVisibleObserver = observe(
  elementY(0, undefined, [resizeEvent(), intervalEvent(2000)]),
  viewportY(0.85),
  viewportY(1.1),
  includeLast(visible)
)

const TextContent = styled.div`
  a {
    color: ${colors.deepBlue};
  }

  ${media.md(css`
    width: calc((7 / 12) * 100%);
    margin-left: calc((2 / 12) * 100%);
  `)}
`

const HeroContainer = styled.div<{
  degrees: string
  color1: string
  color2: string
}>`
  position: relative;
  margin-bottom: 64px;

  ${media.md(css`
    margin-bottom: ${({ theme }) => theme.spacing[6.5]};
  `)}

  &::before {
    position: absolute;
    top: ${between(7.5 * base, 5 * base, {
      fromWidth: breakpoints.xs,
      toWidth: breakpoints.lg,
    })};
    right: 0;
    bottom: 0;
    left: 0;
    content: '';
    display: block;
    z-index: -1;
    ${props => css`
      background: linear-gradient(
        ${props.degrees},
        ${props.color1},
        ${props.color2}
      );
    `};
  }
`

const IntroText = styled.div`
  &::after {
    margin: 0 auto;
    max-width: 114px;
    content: '';
    display: block;
    background: ${colors.deepBlue};
    height: 2px;
    width: 56px;
  }
  ${mediaMax.md(css`
    padding: 16px;
    padding-bottom: ${({ theme }) => theme.spacing[4]};
  `)}
  ${media.md(css`
    padding-bottom: ${({ theme }) => theme.spacing[5]};
    &::after {
      height: 3px;
    }
  `)}
`

const Image = styled.div<{ url: string; offsetLeft?: boolean }>`
  background-size: cover;
  position: relative;
  background-image: url(${({ url }) => url});
  background-position: center;
  height: 60vh;

  ${({ offsetLeft }) =>
    offsetLeft &&
    css`
      margin-left: ${gridOffsetFrom}px;
      ${media.md(css`
        margin-left: ${between(gridOffsetFrom, gridOffsetTo)};
      `)}
    `}

  ${media.md(css`
    max-height: 1100px;
    height: 85vh;
  `)}
`

const isEmpty = childNodes =>
  childNodes && childNodes.length === 1 && childNodes[0] === ''

const documentOptions = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => (
      <Section bottom={{ xs: 0 }} top={{ xs: 0 }}>
        <TextContent>
          <Type as="h2" size={{ xs: 30, md: 40 }} bottom={{ xs: 1, md: 2 }}>
            {children}
          </Type>
        </TextContent>
      </Section>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <Section bottom={{ xs: 0 }} top={{ xs: 0 }}>
        <TextContent>
          <Type
            as="h2"
            size={{ xs: 28, md: 32, lg: 40 }}
            weight="bold"
            bottom={{ xs: 1, md: 2 }}
            top={{ xs: 1, md: 2 }}
          >
            {children}
          </Type>
        </TextContent>
      </Section>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Section bottom={{ xs: 0 }} top={{ xs: 0 }}>
        <TextContent>
          <Type
            as="h3"
            size={{ xs: 23, md: 30, lg: 36 }}
            weight="bold"
            bottom={{ xs: 1, md: 2 }}
          >
            {children}
          </Type>
        </TextContent>
      </Section>
    ),
    [BLOCKS.HEADING_4]: (_, children) => (
      <Section bottom={{ xs: 0 }} top={{ xs: 0 }}>
        <TextContent>
          <Type
            as="h4"
            size={{ xs: 20, md: 28, lg: 32 }}
            weight="bold"
            bottom={{ xs: 1, md: 2 }}
          >
            {children}
          </Type>
        </TextContent>
      </Section>
    ),
    [BLOCKS.PARAGRAPH]: (_, children) =>
      !isEmpty(children) && (
        <Section bottom={{ xs: 0 }} top={{ xs: 0 }}>
          <TextContent>
            <Type
              multiline
              size={{ xs: 18, md: 20, lg: 24 }}
              weight="normal"
              bottom={{ xs: 1, md: 2 }}
            >
              {children}
            </Type>
          </TextContent>
        </Section>
      ),
    [BLOCKS.EMBEDDED_ENTRY]: node => {
      if (!node.data.target) return null

      const { sys, fields } = node.data.target

      switch (sys.contentType.sys.id) {
        case 'storyrelatedExperiences':
          return (
            <RelatedExperiences relatedExperience={{ ...node.data.target }} />
          )
        case 'storyHero':
          return (
            <Section top={{ xs: 2 }} bottom={{ xs: 5 }}>
              <LightBoxItem image={fields.image}>
                <SmartImage image={fields.image}>
                  {imageUrl => <Image url={imageUrl} />}
                </SmartImage>
              </LightBoxItem>
            </Section>
          )
        case 'storygallery':
          return <StoryGallery {...fields} />
        case 'storyBlockQuote':
          return <StoryBlockQuote {...fields} />
        case 'storyVideo':
          return <StoryVideo {...fields} />
        default:
          return null
      }
    },
  },
}

const StoryPage = ({
  publishDate,
  author,
  title,
  tags,
  heroImage,
  introText,
  heroGradient,
  content,
  moreStories = [],
  storyMetadata,
  page,
  locale = 'en', // temporary fix for icelandic stories
}: {
  publishDate: unknown
  author: unknown
  title: string
  tags: unknown
  heroImage: IStoryimage
  introText: string
  heroGradient: IGradient
  content: Document
  moreStories: Entry<IPageStoryFields>[]
  storyMetadata: Partial<IMetadata>
  page: Entry<IPageStoriesFields>
  locale: 'is' | 'en'
}) => {
  const hasRead = useRef(false)
  const readObsRef = useRef(null)

  const readingTime = estimatedReadTime(content)
  const {
    fields: { newsletterSignup },
  } = page
  const { t } = useTranslation()

  const atBottom = useObserver(isVisibleObserver, {
    rootElementRef: readObsRef,
  })

  const renderReadObserver = () => {
    if (atBottom && !hasRead.current) {
      hasRead.current = true
    }

    return <span ref={readObsRef} />
  }

  useEffect(() => {
    triggerPageView({
      language: locale,
      currencyCode: locale === 'en' ? 'EUR' : 'ISK',
    })
  }, [])

  return (
    <Page>
      <LightBoxProvider>
        <Metadata
          metadata={storyMetadata}
          title={title}
          description={introText}
          image={heroImage.fields.image}
        />
        <Section bottom={{ xs: 0 }} top={{ xs: 2.5 }}>
          {locale !== 'is' && (
            <BackToStoriesLink top={{ xs: 2.5 }} bottom={{ md: 1.5 }} />
          )}
          <Type
            as="h1"
            preset="headlineExtraLarge"
            top={{ xs: 1.5 }}
            bottom={{ xs: 1.5 }}
            maxWidth={890}
          >
            {title}
          </Type>
        </Section>
        <StoryMeta
          storyAuthor={author}
          storyPublishDate={publishDate}
          storyReadingTime={readingTime}
        />
        <HeroContainer
          color1={get(
            heroGradient,
            'fields.color1',
            heroGradient.fields.color1
          )}
          color2={get(
            heroGradient,
            'fields.color2',
            heroGradient.fields.color2
          )}
          degrees={get(
            heroGradient,
            'fields.degrees',
            heroGradient.fields.degrees
          )}
        >
          {heroImage && (
            <LightBoxItem image={get(heroImage, 'fields.image')}>
              <SmartImage image={get(heroImage, 'fields.image')}>
                {imageUrl => <Image offsetLeft url={imageUrl} />}
              </SmartImage>
            </LightBoxItem>
          )}
          {introText && (
            <IntroText>
              <Type
                size={{ xs: 20, md: 32, lg: 42 }}
                weight="medium"
                textAlign="center"
                maxWidth={995}
                top={{ xs: 2, md: 5 }}
                bottom={{ xs: 2, md: 3 }}
                style={{ marginLeft: 'auto', marginRight: 'auto' }}
              >
                {introText}
              </Type>
            </IntroText>
          )}
        </HeroContainer>
        <div>
          {!!content && (
            <RichTextRenderer
              document={content}
              customOptions={documentOptions}
            />
          )}
        </div>

        {renderReadObserver()}

        {tags && (
          <TagCollection
            top={{ xs: 2, md: 4 }}
            bottom={{ xs: 2, md: 4 }}
            tags={tags}
            heading="Talks about"
          />
        )}
        {moreStories.length > 0 && (
          <Appear observer>
            <StoryArchive
              label={t('relatedStories')}
              top={{ xs: 2, md: 4 }}
              bottom={{ xs: 2, md: 4 }}
              stories={moreStories}
            />
          </Appear>
        )}
        <ExploreTopics
          heading="Explore other topics"
          description="Discover the waters of the Blue Lagoon. Journey through a spa of the volcanic earth. Harmonize with nature."
        />
        {locale !== 'is' && newsletterSignup && (
          <NewsletterSignupFormSection section={newsletterSignup} />
        )}
      </LightBoxProvider>
    </Page>
  )
}

export default withTransition(StoryPage)
